import { Sensor, Study, StudyFull } from '../models';
import {
  Study as StudyEntity,
  StudyFull as StudyFullEntity,
} from 'adc-sirius-module-common';
import { urls } from '../utilities/dataHelpers';
import {
  mapSensorEntityToSensor,
  mapSiteEntityToSite,
  mapStudyEntityToStudy,
  mapStudyToStudyEntity,
  mapSubjectEntityToSubject,
} from '../utilities/mappers';
import mediator, { MediatorRequestWithResponse } from '../utilities/mediator';
import { DOWNLOAD_CONFIG, DOWNLOAD_JOB_CONFIG, JobStatus } from '../constants';
import i18n from '../utilities/i18n';

const handleGetStudiesByOrganization = (
  request: MediatorRequestWithResponse<Study[]>,
) => {
  const state = request.state;

  request.response = new Promise((resolve, reject) => {
    mediator
      .request<StudyEntity[]>('api:invoke', {
        method: 'GET',
        url: `${urls.api}studies/organization/${request.organizationKey}`,
        queryString: state ? `?row_state=${state}` : '',
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(
            (result.data ?? []).map((studyEntity) =>
              mapStudyEntityToStudy(studyEntity),
            ),
          );
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error fetching studies:', err);
        reject(err);
      });
  });
};

const handleGetStudyByKey = (request: MediatorRequestWithResponse<Study>) => {
  request.response = new Promise((resolve, reject) => {
    const studyKey = request.key;

    mediator
      .request<StudyEntity>('api:invoke', {
        method: 'GET',
        url: `${urls.api}studies/${studyKey}`,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapStudyEntityToStudy(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error fetching study by ID:', err);
        reject(err);
      });
  });
};

const handleCreateStudy = (
  request: MediatorRequestWithResponse<Partial<Study>>,
) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request<StudyEntity>('api:invoke', {
        method: 'POST',
        url: urls.api + 'studies',
        data: mapStudyToStudyEntity(request.data),
      })
      .then((result) => {
        if (result.status === 201) {
          resolve(mapStudyEntityToStudy(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error creating study:', err);
        reject(err);
      });
  });
};

const handleUpdateStudy = (
  request: MediatorRequestWithResponse<Partial<Study>>,
) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request<StudyEntity>('api:invoke', {
        method: 'PUT',
        url: `${urls.api}studies`,
        data: mapStudyToStudyEntity(request.data),
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapStudyEntityToStudy(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error updating study:', err);
        reject(err);
      });
  });
};

const handleGetStudyFullByKey = (
  request: MediatorRequestWithResponse<StudyFull>,
) => {
  request.response = new Promise((resolve, reject) => {
    const studyKey = request.key;

    mediator
      .request<StudyFullEntity>('api:invoke', {
        method: 'GET',
        url: `${urls.api}studies/${studyKey}/full`,
      })
      .then((result) => {
        if (result.status === 200) {
          const subjects = result.data.subjects ?? [];
          const sites = result.data.sites ?? [];
          const sensorsBySubject = result.data.sensors ?? {};
          const sensors = new Map<string, Sensor[]>();
          const conditions = new Set<string>();

          subjects.forEach(({ key }) => {
            const sensorsForSubject = sensorsBySubject[key] || [];

            sensors.set(key, sensorsForSubject.map(mapSensorEntityToSensor));
            sensorsForSubject.forEach((sensor) => {
              conditions.add(sensor.ClinicalSensorDetails.conditionCode);
            });
          });

          resolve({
            study: mapStudyEntityToStudy(result.data.study),
            sites: sites.map(mapSiteEntityToSite),
            subjects: subjects.map(mapSubjectEntityToSubject),
            sensors,
            conditions: Array.from(conditions),
          });
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        console.error('Error fetching full study by key:', err);
        reject(err);
      });
  });
};

const handleDownloadStudyData = (
  request: MediatorRequestWithResponse<Blob>,
) => {
  request.response = new Promise((resolve, reject) => {
    const { subjectKeys } = request;

    if (!subjectKeys || subjectKeys.length === 0) {
      reject(new Error());
      return;
    }

    const processDownload = async () => {
      try {
        // Step 1: Create download job
        const jobResponse = await mediator.request<{
          jobKey: string;
          status: JobStatus;
          queuedAtUtc: number;
        }>('api:invoke', {
          method: 'POST',
          url: `${urls.api}subjects/download-job`,
          data: {
            subjectKeys: subjectKeys,
            outputFormat: DOWNLOAD_CONFIG.OUTPUT_FORMAT,
          },
        });

        if (jobResponse.status === 429) {
          console.error(
            'Rate limited creating download job:',
            jobResponse.status,
          );
          throw new Error(i18n.t('Modal_Download.error_rate_limit'));
        }

        if (jobResponse.status !== 202) {
          console.error('Error creating download job:', jobResponse.status);
          throw new Error();
        }

        const { jobKey } = jobResponse.data;

        // Step 2: Poll job status until complete
        const pollStatus = async (): Promise<void> => {
          const statusResponse = await mediator.request<{
            jobKey: string;
            status: JobStatus;
            progressPercent: number;
            failureReason?: string;
            completedAtUtc?: number;
          }>('api:invoke', {
            method: 'GET',
            url: `${urls.api}subjects/download-job/${jobKey}/status`,
          });

          if (statusResponse.status !== 200) {
            console.error('Error checking job status:', statusResponse.status);
            throw new Error();
          }

          const { status, progressPercent } = statusResponse.data;

          // Notify progress if callback provided
          if (request.onProgress) {
            request.onProgress(progressPercent || 0);
          }

          if (status === JobStatus.COMPLETE) {
            // Step 3: Download the actual file once job is complete
            const downloadResult = await mediator.request<Blob>('api:invoke', {
              method: 'GET',
              url: `${urls.api}subjects/download-job/${jobKey}`,
              responseType: 'blob',
            });

            // Success: API returns blob directly
            if (downloadResult instanceof Blob) {
              resolve(downloadResult);
            } else {
              console.error(
                'Error downloading file:',
                downloadResult?.['status'],
              );
              throw new Error();
            }
          } else if (status === JobStatus.FAILED) {
            console.error(
              'Download job failed:',
              statusResponse.data.failureReason,
            );
            throw new Error();
          } else {
            // Job still in progress (queued, processing, or other status)
            setTimeout(() => {
              pollStatus().catch(reject);
            }, DOWNLOAD_JOB_CONFIG.POLL_INTERVAL_MS);
          }
        };

        // Start polling
        await pollStatus();
      } catch (error) {
        console.error('Error downloading study data:', error);
        reject(error);
      }
    };

    // Start the async process
    processDownload();
  });
};

mediator.subscribe('study:getStudies', handleGetStudiesByOrganization);
mediator.subscribe('study:getStudyByKey', handleGetStudyByKey);
mediator.subscribe('study:createStudy', handleCreateStudy);
mediator.subscribe('study:updateStudy', handleUpdateStudy);
mediator.subscribe('study:getStudyFullByKey', handleGetStudyFullByKey);
mediator.subscribe('study:downloadData', handleDownloadStudyData);
